import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { MdTune } from 'react-icons/md';
import { Button, DialogField, DialogFieldLabel, TagPill } from '@aibuild/ui';
import LlmModelPicker from '@components/Chat/LlmModelPicker';
import { LLM_MODEL_AUTO } from '@utils/llmModel';
import { FieldHint, SkillList, SkillRow } from './AppAgentFields.styled';
import AppSkillChoiceField from './AppSkillChoiceField';
import AppSkillsPicker from './AppSkillsPicker';
import { useAppSkills } from './useAppBuilder';

interface AppAgentFieldsProps {
  readonly allowedSkills: readonly string[];
  readonly alternativeSkills: readonly string[];
  /** Empty when the app runs on the default model. */
  readonly llmModel: string;
  readonly onChange: (patch: {
    allowedSkills?: readonly string[];
    alternativeSkills?: readonly string[];
    llmModel?: string;
  }) => void;
}

const AppAgentFields: React.FC<AppAgentFieldsProps> = ({ allowedSkills, alternativeSkills, llmModel, onChange }) => {
  const intl = useIntl();
  const skills = useAppSkills();
  const [picking, setPicking] = useState(false);

  const byId = new Map((skills.data ?? []).map((skill) => [skill.id, skill]));
  const unavailable = intl.formatMessage({
    id: 'apps.builder.details.skills.unavailable',
    defaultMessage: 'Unavailable skill',
  });
  const pillLabel = (id: string): string => {
    if (!skills.data) {
      return id;
    }
    return byId.get(id)?.name ?? unavailable;
  };

  return (
    <>
      <DialogField>
        <DialogFieldLabel as="span">
          {intl.formatMessage({ id: 'apps.builder.details.model', defaultMessage: 'Model' })}
        </DialogFieldLabel>
        <LlmModelPicker
          llmModel={llmModel || LLM_MODEL_AUTO}
          menuDirection="down"
          appearance="field"
          onPick={(model) => onChange({ llmModel: model === LLM_MODEL_AUTO ? '' : model })}
        />
      </DialogField>
      <DialogField>
        <DialogFieldLabel as="span">
          {intl.formatMessage({ id: 'apps.builder.details.skills', defaultMessage: 'Skills' })}
        </DialogFieldLabel>
        <SkillRow>
          {allowedSkills.length > 0 ? (
            <SkillList data-testid="app-builder__skills">
              {allowedSkills.map((skill) => (
                <TagPill key={skill} label={pillLabel(skill)} prefix={false} size="sm" />
              ))}
            </SkillList>
          ) : (
            <FieldHint data-testid="app-builder__skills">
              {intl.formatMessage({
                id: 'apps.builder.details.skills.none',
                defaultMessage: 'None. The agent works with its general tools: Python, files and images.',
              })}
            </FieldHint>
          )}
          <Button
            variant="outlined"
            size="sm"
            leadingIcon={<MdTune size={16} aria-hidden="true" />}
            loading={skills.isLoading}
            disabled={skills.isError}
            onClick={() => setPicking(true)}
            dataTestId="app-builder__skills-open"
          >
            {intl.formatMessage({ id: 'apps.builder.details.skills.choose', defaultMessage: 'Choose skills' })}
          </Button>
        </SkillRow>
      </DialogField>
      <AppSkillsPicker
        open={picking}
        onClose={() => setPicking(false)}
        skills={skills.data ?? []}
        selected={allowedSkills}
        onConfirm={(selected) => onChange({ allowedSkills: selected })}
      />
      {allowedSkills.length >= 2 && (
        <AppSkillChoiceField
          allowedSkills={allowedSkills}
          alternativeSkills={alternativeSkills}
          skillName={pillLabel}
          onChange={(chosen) => onChange({ alternativeSkills: chosen })}
        />
      )}
    </>
  );
};

export default AppAgentFields;
